/**
 * 이메일 인증 모달 — contact/franchise 공통 동작.
 *
 * 책임:
 *  - "이메일 인증" 버튼 클릭 → POST /email/verify/send → 모달 오픈 + 카운트다운 시작
 *  - "인증하기" 버튼 → POST /email/verify/check → 성공 시 email_verified 히든 = "1", 라벨 노출
 *  - "인증메일 재발송" → 60초 쿨다운(서버 응답 + 버튼 disabled)
 *  - 이메일 입력값 변경 시 인증 상태 초기화(히든=0, 라벨 숨김)
 *
 * 페이지 코드는 한 줄만 — new EmailVerificationModal({...}).init();
 *
 * 마크업: resources/views/components/email-verify-modal.php (data-email-verify-modal="<id>")
 */

export interface EmailVerificationModalOptions {
  /** 폼 엘리먼트(필수) — 이 폼 안의 email 인풋/히든/라벨을 찾는다 */
  form: HTMLFormElement;
  /** 폼 내부 이메일 input 셀렉터 */
  emailInputSelector: string;
  /** 인증 완료 후 1로 바뀔 히든 input 셀렉터 (form 내부) */
  verifiedHiddenSelector: string;
  /** "인증 완료" 라벨 셀렉터 (form 내부, hidden 토글) */
  verifiedLabelSelector: string;
  /** "이메일 인증" 트리거 버튼 셀렉터 (form 내부) */
  openButtonSelector: string;
  /** 서버측 용도 식별자 — contact_inquiry | contact_history | franchise */
  purpose: string;
  /** 모달 식별자(여러 폼 공존 시 분리용) — 기본 'email-verify-modal' */
  modalId?: string;
  /** 인증 완료 콜백 */
  onVerified?: (email: string) => void;
  /** POST 엔드포인트 — 라우트 명과 1:1, 기본 표준 경로 */
  sendEndpoint?: string;
  checkEndpoint?: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
  data?: Record<string, unknown> | null;
  errors?: Record<string, unknown> | null;
  meta?: Record<string, unknown> | null;
}

import { t } from "../utils/I18n";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function csrfHeader(): Record<string, string> {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
  const token = meta?.content?.trim();
  return token ? { "X-CSRF-TOKEN": token } : {};
}

function formatMmSs(secondsLeft: number): string {
  const s = Math.max(0, Math.floor(secondsLeft));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

export class EmailVerificationModal {
  private readonly opts: EmailVerificationModalOptions;

  private modal!: HTMLElement;
  private addressEl: HTMLElement | null = null;
  private codeInput!: HTMLInputElement;
  private timerEl: HTMLElement | null = null;
  private resendBtn: HTMLButtonElement | null = null;
  private submitBtn!: HTMLButtonElement;
  private closeBtns!: NodeListOf<HTMLElement>;
  private messageEl: HTMLElement | null = null;

  private emailInput!: HTMLInputElement;
  private verifiedHidden!: HTMLInputElement;
  private verifiedLabel!: HTMLElement;
  private openBtn!: HTMLButtonElement;

  private countdownTimer: number | null = null;
  private cooldownTimer: number | null = null;
  private lastSentEmail = "";
  private isSending = false;
  private isChecking = false;

  constructor(opts: EmailVerificationModalOptions) {
    this.opts = opts;
  }

  init(): this {
    const modalId = this.opts.modalId ?? "email-verify-modal";
    const modal = document.querySelector<HTMLElement>(
      `[data-email-verify-modal="${modalId}"]`,
    );
    if (!modal) return this; // 페이지에 모달 파셜이 없으면 조용히 종료
    this.modal = modal;

    const emailInput = this.opts.form.querySelector<HTMLInputElement>(
      this.opts.emailInputSelector,
    );
    const verifiedHidden = this.opts.form.querySelector<HTMLInputElement>(
      this.opts.verifiedHiddenSelector,
    );
    const verifiedLabel = this.opts.form.querySelector<HTMLElement>(
      this.opts.verifiedLabelSelector,
    );
    const openBtn = this.opts.form.querySelector<HTMLButtonElement>(
      this.opts.openButtonSelector,
    );
    if (!emailInput || !verifiedHidden || !verifiedLabel || !openBtn) return this;

    this.emailInput = emailInput;
    this.verifiedHidden = verifiedHidden;
    this.verifiedLabel = verifiedLabel;
    this.openBtn = openBtn;

    this.addressEl = modal.querySelector<HTMLElement>("[data-email-verify-address]");
    this.codeInput = modal.querySelector<HTMLInputElement>("[data-email-code]")!;
    this.timerEl = modal.querySelector<HTMLElement>("[data-email-verify-timer]");
    this.resendBtn = modal.querySelector<HTMLButtonElement>("[data-email-verify-resend]");
    this.submitBtn = modal.querySelector<HTMLButtonElement>("[data-email-verify-submit]")!;
    this.closeBtns = modal.querySelectorAll<HTMLElement>("[data-email-verify-close]");
    this.messageEl = modal.querySelector<HTMLElement>("[data-email-verify-message]");

    this.wire();
    return this;
  }

  /** 외부에서 인증 상태 강제 초기화(예: 폼 reset 후) */
  resetVerified(): void {
    this.verifiedHidden.value = "0";
    this.verifiedLabel.hidden = true;
    this.lastSentEmail = "";
    this.openBtn.classList.remove("is-verified");
    // textContent 대신 텍스트 노드만 교체 — spinner span(data-form-loader) 보존
    for (const node of Array.from(this.openBtn.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) {
        node.textContent = t("email_verify.verify") + " ";
        break;
      }
    }
    this.openBtn.disabled = false;
  }

  /** 외부에서 호출 — 폼 제출 직전 인증 완료 여부 확인 */
  isVerified(): boolean {
    return this.verifiedHidden.value === "1";
  }

  private wire(): void {
    this.openBtn.addEventListener("click", () => this.handleOpen());

    this.emailInput.addEventListener("input", () => this.resetVerified());

    this.closeBtns.forEach((el) =>
      el.addEventListener("click", () => this.closeModal()),
    );
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.modal.classList.contains("is-open")) {
        this.closeModal();
      }
    });

    if (this.resendBtn) {
      this.resendBtn.addEventListener("click", () => this.handleResend());
    }

    this.submitBtn.addEventListener("click", () => this.handleSubmit());

    // 6자리 숫자만
    this.codeInput.addEventListener("input", () => {
      this.codeInput.value = this.codeInput.value.replace(/\D/g, "").slice(0, 6);
    });
  }

  private async handleOpen(): Promise<void> {
    if (this.isVerified()) return;
    const email = this.emailInput.value.trim();
    if (!EMAIL_PATTERN.test(email)) {
      this.emailInput.focus();
      return;
    }
    // 같은 이메일로 이미 코드가 발송된 상태면 재발송 없이 모달만 다시 열기
    if (this.lastSentEmail === email) {
      this.openModal();
      return;
    }
    await this.sendCode(email);
  }

  private async handleResend(): Promise<void> {
    const email = this.emailInput.value.trim();
    if (!EMAIL_PATTERN.test(email)) {
      this.showMessage(t("email_verify.check_email"));
      return;
    }
    await this.sendCode(email);
  }

  private setOpenBtnLoading(loading: boolean): void {
    this.openBtn.disabled = loading;
    const loader = this.openBtn.querySelector<HTMLElement>("[data-form-loader]");
    if (loader) {
      loader.style.display = loading ? "inline-block" : "none";
    }
  }

  private setSubmitBtnLoading(loading: boolean): void {
    this.submitBtn.disabled = loading;
    const loader = this.submitBtn.querySelector<HTMLElement>("[data-form-loader]");
    if (loader) {
      loader.style.display = loading ? "inline-block" : "none";
    }
  }

  private async sendCode(email: string): Promise<void> {
    if (this.isSending) return;
    this.isSending = true;
    this.setResendDisabled(true);
    this.setOpenBtnLoading(true);

    try {
      const url = this.opts.sendEndpoint ?? "/email/verify/send";
      const body = new FormData();
      body.append("email", email);
      body.append("purpose", this.opts.purpose);

      const res = await fetch(url, {
        method: "POST",
        body,
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          ...csrfHeader(),
        },
      });
      const json = (await res.json().catch(() => ({}))) as ApiResponse;

      if (!res.ok || !json.success) {
        // 429(쿨다운)도 동일 메시지 — 서버 메시지 그대로
        this.showMessage(json.message ?? t("email_verify.send_failed"));
        // 쿨다운 응답이면 남은 시간만큼 재발송 버튼 잠금
        const meta = json.meta as { cooldown?: number } | null | undefined;
        const cooldown = (json.data as { cooldown?: number } | undefined)?.cooldown
          ?? meta?.cooldown
          ?? 0;
        this.startCooldown(Number(cooldown) || 0);
        return;
      }

      this.lastSentEmail = email;
      this.codeInput.value = "";
      this.clearMessage();
      if (this.addressEl) this.addressEl.textContent = email;
      this.openModal();

      const validMinutes = Number(
        (json.data as { valid_minutes?: number } | undefined)?.valid_minutes ?? 10,
      );
      this.startCountdown(validMinutes * 60);

      const cooldown = Number(
        (json.data as { cooldown?: number } | undefined)?.cooldown ?? 60,
      );
      this.startCooldown(cooldown);
    } catch {
      this.showMessage(t("email_verify.send_network_error"));
    } finally {
      this.isSending = false;
      this.setOpenBtnLoading(false);
    }
  }

  private async handleSubmit(): Promise<void> {
    if (this.isChecking) return;
    const code = this.codeInput.value.trim();
    if (code === "") {
      this.showMessage(t("email_verify.code_required"));
      this.codeInput.focus();
      return;
    }
    const email = this.lastSentEmail || this.emailInput.value.trim();
    if (!EMAIL_PATTERN.test(email)) {
      this.showMessage(t("email_verify.check_email"));
      return;
    }
    this.isChecking = true;
    this.setSubmitBtnLoading(true);

    try {
      const url = this.opts.checkEndpoint ?? "/email/verify/check";
      const body = new FormData();
      body.append("email", email);
      body.append("purpose", this.opts.purpose);
      body.append("code", code);

      const res = await fetch(url, {
        method: "POST",
        body,
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          ...csrfHeader(),
        },
      });
      const json = (await res.json().catch(() => ({}))) as ApiResponse;

      if (!res.ok || !json.success) {
        this.showMessage(json.message ?? t("email_verify.code_mismatch"));
        return;
      }

      this.verifiedHidden.value = "1";
      this.verifiedLabel.hidden = false;
      this.stopCountdown();
      this.openBtn.classList.add("is-verified");
      for (const node of Array.from(this.openBtn.childNodes)) {
        if (node.nodeType === Node.TEXT_NODE) {
          node.textContent = t("email_verify.verified") + " ";
          break;
        }
      }
      this.openBtn.disabled = true;
      this.closeModal();
      if (this.opts.onVerified) this.opts.onVerified(email);
    } catch {
      this.showMessage(t("email_verify.verify_network_error"));
    } finally {
      this.isChecking = false;
      this.setSubmitBtnLoading(false);
    }
  }

  private openModal(): void {
    this.modal.classList.add("is-open");
    this.modal.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("is-email-verify-open");
    // 코드 입력 포커스
    window.setTimeout(() => this.codeInput.focus(), 50);
  }

  private closeModal(): void {
    this.modal.classList.remove("is-open");
    this.modal.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("is-email-verify-open");
  }

  private startCountdown(totalSeconds: number): void {
    this.stopCountdown();
    if (!this.timerEl) return;
    let left = totalSeconds;
    this.timerEl.textContent = formatMmSs(left);
    this.countdownTimer = window.setInterval(() => {
      left -= 1;
      if (this.timerEl) this.timerEl.textContent = formatMmSs(left);
      if (left <= 0) {
        this.stopCountdown();
        // 만료 — 서버도 동일 시각에 expires_at 으로 거절하므로 추가 처리 불필요
      }
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.countdownTimer !== null) {
      window.clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private startCooldown(seconds: number): void {
    if (!this.resendBtn) return;
    if (this.cooldownTimer !== null) {
      window.clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
    if (seconds <= 0) {
      this.setResendDisabled(false);
      return;
    }
    let left = seconds;
    const originalText = t("email_verify.resend");
    this.resendBtn.textContent = `${originalText} (${left}s)`;
    this.setResendDisabled(true);
    this.cooldownTimer = window.setInterval(() => {
      left -= 1;
      if (!this.resendBtn) return;
      if (left <= 0) {
        if (this.cooldownTimer !== null) {
          window.clearInterval(this.cooldownTimer);
          this.cooldownTimer = null;
        }
        this.resendBtn.textContent = originalText;
        this.setResendDisabled(false);
      } else {
        this.resendBtn.textContent = `${originalText} (${left}s)`;
      }
    }, 1000);
  }

  private setResendDisabled(disabled: boolean): void {
    if (this.resendBtn) this.resendBtn.disabled = disabled;
  }

  private showMessage(text: string, ok = false): void {
    if (!this.messageEl) return;
    this.messageEl.textContent = text;
    this.messageEl.classList.toggle("--ok", ok);
    this.messageEl.hidden = false;
  }

  private clearMessage(): void {
    if (!this.messageEl) return;
    this.messageEl.textContent = "";
    this.messageEl.hidden = true;
  }
}
